import {useState, useEffect, useMemo} from 'react'
import {Field, Label} from '@/components/tailwind/fieldset'
import {Select} from '@/components/tailwind/select'
import {Input} from '@/components/tailwind/input'
import {useTranslation} from '@/i18n/hooks'
import type {Namespace} from '@/i18n/types'

export type TimePeriodPreset = 'year' | 'quarter' | 'month' | 'custom' | 'fullAccount';

export interface TimePeriod {
    startDate: string;
    endDate: string;
}

interface TimePeriodSelectorProps {
    earliestDate?: string | null;
    latestDate?: string | null;
    // Null means no range (the whole account).
    onChange: (period: TimePeriod | null) => void;
    translationNamespace?: Namespace;
    translationKeyPrefix?: string;
    includeFullAccount?: boolean;
    defaultPreset?: TimePeriodPreset;
}

function getYearRange(earliestDate: string | null, latestDate: string | null): number[] {
    const currentYear = new Date().getFullYear()
    const startYear = earliestDate ? new Date(earliestDate).getFullYear() : currentYear - 5
    const endYear = latestDate ? new Date(latestDate).getFullYear() : currentYear

    const years: number[] = []
    for (let year = endYear; year >= startYear; year--) {
        years.push(year)
    }
    return years
}

function getQuarterDates(year: number, quarter: number): TimePeriod {
    const startMonth = (quarter - 1) * 3
    const endMonth = startMonth + 2
    const startDate = new Date(year, startMonth, 1)
    const endDate = new Date(year, endMonth + 1, 0)

    return {
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
    }
}

function getMonthDates(year: number, month: number): TimePeriod {
    const startDate = new Date(year, month - 1, 1)
    const endDate = new Date(year, month, 0)

    return {
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
    }
}

function getYearDates(year: number): TimePeriod {
    return {
        startDate: `${year}-01-01`,
        endDate: `${year}-12-31`,
    }
}

// Local calendar date; toISOString would shift the day across time zones.
function formatDate(date: Date): string {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

export function TimePeriodSelector({
    earliestDate,
    latestDate,
    onChange,
    translationNamespace = 'reports',
    translationKeyPrefix = 'statistics.timePeriod',
    includeFullAccount = false,
    defaultPreset = 'year',
}: TimePeriodSelectorProps) {
    const {t} = useTranslation(translationNamespace)
    const key = (suffix: string) => `${translationKeyPrefix}.${suffix}`
    const currentYear = new Date().getFullYear()

    const [preset, setPreset] = useState<TimePeriodPreset>(defaultPreset)
    const [selectedYear, setSelectedYear] = useState(currentYear)
    const [selectedQuarter, setSelectedQuarter] = useState(1)
    const [selectedMonth, setSelectedMonth] = useState(1)
    const [customStartDate, setCustomStartDate] = useState('')
    const [customEndDate, setCustomEndDate] = useState('')

    const years = useMemo(
        () => getYearRange(earliestDate ?? null, latestDate ?? null),
        [earliestDate, latestDate]
    )

    useEffect(() => {
        let period: TimePeriod | null

        switch (preset) {
            case 'fullAccount':
                period = null
                break
            case 'year':
                period = getYearDates(selectedYear)
                break
            case 'quarter':
                period = getQuarterDates(selectedYear, selectedQuarter)
                break
            case 'month':
                period = getMonthDates(selectedYear, selectedMonth)
                break
            case 'custom':
                if (customStartDate && customEndDate) {
                    period = {startDate: customStartDate, endDate: customEndDate}
                } else {
                    return
                }
                break
        }

        onChange(period)
    }, [preset, selectedYear, selectedQuarter, selectedMonth, customStartDate, customEndDate, onChange])

    const months = [
        {value: 1, label: 'Januar'},
        {value: 2, label: 'Februar'},
        {value: 3, label: 'März'},
        {value: 4, label: 'April'},
        {value: 5, label: 'Mai'},
        {value: 6, label: 'Juni'},
        {value: 7, label: 'Juli'},
        {value: 8, label: 'August'},
        {value: 9, label: 'September'},
        {value: 10, label: 'Oktober'},
        {value: 11, label: 'November'},
        {value: 12, label: 'Dezember'},
    ]

    const quarters = [
        {value: 1, label: 'Q1 (Jan-Mär)'},
        {value: 2, label: 'Q2 (Apr-Jun)'},
        {value: 3, label: 'Q3 (Jul-Sep)'},
        {value: 4, label: 'Q4 (Okt-Dez)'},
    ]

    const showSubSelectors = preset !== 'custom' && preset !== 'fullAccount'

    return (
        <div className="space-y-4">
            <Field>
                <Label>{t(key('label'))}</Label>
                <Select
                    value={preset}
                    onChange={(e) => setPreset(e.target.value as TimePeriodPreset)}
                >
                    {includeFullAccount && (
                        <option value="fullAccount">{t(key('presets.fullAccount'))}</option>
                    )}
                    <option value="year">{t(key('presets.year'))}</option>
                    <option value="quarter">{t(key('presets.quarter'))}</option>
                    <option value="month">{t(key('presets.month'))}</option>
                    <option value="custom">{t(key('presets.custom'))}</option>
                </Select>
            </Field>

            {showSubSelectors && (
                <Field>
                    <Label>{t(key('presets.year'))}</Label>
                    <Select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    >
                        {years.map((year) => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </Select>
                </Field>
            )}

            {preset === 'quarter' && (
                <Field>
                    <Label>{t(key('presets.quarter'))}</Label>
                    <Select
                        value={selectedQuarter}
                        onChange={(e) => setSelectedQuarter(parseInt(e.target.value))}
                    >
                        {quarters.map((q) => (
                            <option key={q.value} value={q.value}>{q.label}</option>
                        ))}
                    </Select>
                </Field>
            )}

            {preset === 'month' && (
                <Field>
                    <Label>{t(key('presets.month'))}</Label>
                    <Select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    >
                        {months.map((m) => (
                            <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                    </Select>
                </Field>
            )}

            {preset === 'custom' && (
                <div className="grid grid-cols-2 gap-4">
                    <Field>
                        <Label>{t(key('startDate'))}</Label>
                        <Input
                            type="date"
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                            min={earliestDate || undefined}
                            max={latestDate || undefined}
                        />
                    </Field>
                    <Field>
                        <Label>{t(key('endDate'))}</Label>
                        <Input
                            type="date"
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                            min={customStartDate || earliestDate || undefined}
                            max={latestDate || undefined}
                        />
                    </Field>
                </div>
            )}
        </div>
    )
}
